import uuid from "react-uuid";
import axios from "axios";
import CryptoJS from "crypto-js";
import Base64 from "crypto-js/enc-base64";
import Cookies from "js-cookie";
import { s360ConfigService } from "./S360Config";

import endpoints from "../../utils/Constants/endpoints";
import {
  getCookieForContactDetails,
  setCookieForPrimaryIdentification,
} from "../Conversation/utils/ConversationUtils";
import { statsigLogEvent } from "@nextiva/ncx-feature-management";
import {
  LIVECHAT_LOG_ACTIONS,
  LIVECHAT_LOG_EVENTS,
  LIVECHAT_LOG_KEYS,
} from "../../utils/Constants/statsig.constants";

class APIService {
  constructor() {
    this.uuidForFrom = uuid(); // Initialize with a UUID for 'from' value
    if (!APIService.shared) {
      APIService.shared = this;
    }
    return APIService.shared;
  }

  getUUidIfNoPrimaryIdentification() {
    if (!this.uuidForFrom) {
      this.uuidForFrom = uuid();
    }
    return this.uuidForFrom;
  }

  getFromUuidValue() {
    const widgetconfigId = s360ConfigService?.config?.widgetconfigId;
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (widgetconfigId) {
      const existing = getCookieForContactDetails(widgetconfigId);
      if (existing && uuidPattern.test(existing)) {
        this.uuidForFrom = existing;
        return existing;
      }
    }

    if (!this.uuidForFrom || !uuidPattern.test(this.uuidForFrom)) {
      this.uuidForFrom = uuid();
    }

    if (widgetconfigId) {
      setCookieForPrimaryIdentification(this.uuidForFrom, widgetconfigId);
    }

    return this.uuidForFrom;
  }
  // Check if the UUID is already set, if not, generate a new one
  logInWithFormData = (formDetails) => {
    return this.logIn({ form: formDetails });
  };

  logInWithUserData = (chatUserModel) => {
    return this.logIn({ user: chatUserModel });
  };

  get thrioAuthHeaders() {
    const token = s360ConfigService?.config?.token;
    if (!token) {
      console.error("APIService Error: Thrio token is missing in config.");

      return { headers: {} };
    }
    return {
      headers: {
        Authorization: token,
      },
    };
  }

  getAnonymousUserId(widgetConfigId) {
    if (!widgetConfigId) return null;
    const cookieName = `nextiva-identification-${widgetConfigId}`;
    const existingCookie = Cookies.get(cookieName);
    return existingCookie || null; // Return null if cookie not found
  }

  findEmailKey(obj) {
    if (!obj || typeof obj !== "object") return null; // Added type check
    const lowerCaseKeys = Object.keys(obj).map((key) => key.toLowerCase());
    const emailKeys = ["email", "emailid", "emailaddress"];

    for (const emailKey of emailKeys) {
      const index = lowerCaseKeys.indexOf(emailKey);
      if (index !== -1) {
        return Object.keys(obj)[index]; // Return the original case key
      }
    }
    return null;
  }

  submitFormData(formDetails, dynamicFormData) {
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token ||
      !s360ConfigService?.config?.tenantId
    ) {
      console.error(
        "APIService Error: Missing configuration for submitFormData (location, token, or tenantId).",
      );
      return Promise.reject(
        new Error("Missing configuration for submitFormData"),
      );
    }
    let fromValue = this.getFromUuidValue();

    let payload = {}; // Initialize payload

    // Helper function - consider moving outside if used elsewhere or simplifying
    // function checkAndGetFormInfo() {
    //   let formInfo = {};
    //   if(dynamicFormData && dynamicFormData[0] && dynamicFormData[0].elements) {
    //      dynamicFormData[0].elements.forEach((item) => { // Use forEach instead of map for side effects
    //        if(item?.properties?.label && item?.properties?.fieldname && formDetails[item.properties.fieldname]) {
    //           formInfo[item.properties.label] = formDetails[item.properties.fieldname];
    //        }
    //      });
    //   }
    //   return formInfo;
    // }

    if (
      dynamicFormData &&
      Array.isArray(dynamicFormData) &&
      dynamicFormData.length > 0 &&
      dynamicFormData[0]?.elements
    ) {
      const formInfo = dynamicFormData[0].elements.reduce((acc, item) => {
        // Ensure item and properties exist before accessing
        if (
          item?.properties?.label &&
          item?.properties?.fieldname &&
          formDetails.hasOwnProperty(item.properties.fieldname)
        ) {
          acc[item.properties.label] = formDetails[item.properties.fieldname];
          acc[item.properties.fieldname] =
            formDetails[item.properties.fieldname];
        }
        return acc;
      }, {});

      // Prefer using findEmailKey for robustness
      const emailKey = this.findEmailKey(formInfo);
      const emailValue = emailKey ? formInfo[emailKey] : null;

      if (emailValue) {
        payload = {
          from: fromValue,
          connections: [
            {
              type: "Email",
              // Use nullish coalescing for cleaner default empty strings
              name: `${formInfo["First Name"] ?? ""} ${
                formInfo["Last Name"] ?? ""
              }`.trim(),
              value: emailValue,
              ...formDetails,
            },
          ],
          tenantId: s360ConfigService.config.tenantId,
        };
        // Safely update nested config object
        s360ConfigService.config.s360UserConfig =
          s360ConfigService.config.s360UserConfig || {};
        // s360ConfigService.config.s360UserConfig.EmailId = emailValue;
      } else {
        // Fallback if no email in formInfo but dynamicFormData exists
        payload = {
          ...formDetails,
          from: fromValue,
          tenantId: s360ConfigService.config.tenantId,
        };
      }
    } else {
      // Handle case where dynamicFormData is not provided or invalid
      // Use s360UserConfig if available, otherwise try formDetails directly
      const userConfigEmail =
        s360ConfigService?.config?.s360UserConfig?.EmailId;
      const formDetailsEmailKey = this.findEmailKey(formDetails);
      const formDetailsEmailValue = formDetailsEmailKey
        ? formDetails[formDetailsEmailKey]
        : null;
      const connectToNextiva = s360ConfigService?.getConfig()?.connectToNextiva; // Cache getConfig result

      const finalEmailValue = userConfigEmail || formDetailsEmailValue;

      if (finalEmailValue) {
        payload = {
          from: fromValue,
          tenantId: s360ConfigService.config.tenantId,
          connections: [
            {
              // Name could be empty if only email is available
              name: "Email", // Or potentially derive from formDetails if 'First Name'/'Last Name' keys exist
              type: "Email",
              value: finalEmailValue,
            },
          ],
        };
        // If using Nextiva specific logic was intended, it might need refinement based on actual requirements
        // The original 'else if (connectToNextiva)' logic seemed potentially redundant if finalEmailValue is primary source.
      } else {
        payload = {
          from: fromValue,
          tenantId: s360ConfigService.config.tenantId,
        };
        console.warn(
          "Could not determine email for connections in submitFormData",
        );
      }
    }

    if (payload.from && s360ConfigService?.config?.widgetconfigId) {
      setCookieForPrimaryIdentification(
        payload.from,
        s360ConfigService.config.widgetconfigId,
      );
    }

    statsigLogEvent(LIVECHAT_LOG_EVENTS.SUBMIT_ONLINE_FORM, {
      [LIVECHAT_LOG_KEYS.action]: LIVECHAT_LOG_ACTIONS.SUBMIT_ONLINE_FORM,
      [LIVECHAT_LOG_KEYS.widgetConfigId]:
        s360ConfigService?.config?.widgetconfigId,
      [LIVECHAT_LOG_KEYS.timeStamp]: Date.now(),
      // [LIVECHAT_LOG_KEYS.otherDetails]: { ...payload },
    });

    // return axios.post(
    //   s360ConfigService.config.location + endpoints.SUBMIT_FORM_DATA,
    //   payload,
    //   this.thrioAuthHeaders, // Use getter for headers
    // );

    return Promise.resolve(payload); // Return the constructed payload for testing/verification instead of making an actual API call
  }

  initPreview() {
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.previewSessionId
    ) {
      console.error(
        "APIService Error: Missing configuration for initPreview (location or previewSessionId).",
      );
      return Promise.reject(new Error("Missing configuration for initPreview"));
    }
    const payload = {
      // Ensure previewSessionId is decoded safely
      previewSessionId: decodeURIComponent(
        s360ConfigService.config.previewSessionId,
      ),
    };
    return axios.post(
      s360ConfigService.config.location + endpoints.PREVIEW_INIT,
      payload,
      this.thrioAuthHeaders, // Use getter for headers
    );
  }

  async initiateChat(
    tenant,
    campaignId,
    formDetails,
    dynamicFormData,
    isOffline = false,
  ) {
    if (s360ConfigService.config.sandBox) {
      return;
    }

    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token ||
      !tenant ||
      !campaignId ||
      !s360ConfigService?.config?._id ||
      !s360ConfigService?.config?.widgetconfigId
    ) {
      console.error(
        "APIService Error: Missing configuration or parameters for initiateChat.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for initiateChat"),
      );
    }

    let firstName = ""; // Initialize with default empty string
    let lastName = "";
    let email = "";
    let phone = "";
    const isAnonymous = true;

    // Await config directly if getConfig is async, otherwise access directly
    const userConfig = s360ConfigService?.config?.s360UserConfig; // Assuming config is already loaded

    const configDetails = s360ConfigService?.config?.configDetails;
    const isFormHidden = !configDetails?.showForm;
    const isExplicitlyAnonymous =
      !s360ConfigService?.config?.connectToNextiva &&
      s360ConfigService?.config?.anonymous;

    if (isFormHidden || isExplicitlyAnonymous) {
      const id = "";
      firstName = userConfig?.agentName ?? id;
      lastName = userConfig?.agentName ? "" : id;
      email = id;
      phone = id;
      s360ConfigService.thrioConfig = s360ConfigService.thrioConfig || {};
      s360ConfigService.thrioConfig.formDetails = {
        "First Name": firstName,
        "Last Name": lastName,
        Email: email,
        "Phone Number": phone,
      };
    }

    let formInfo = {};
    if (
      dynamicFormData &&
      formDetails &&
      Array.isArray(dynamicFormData) &&
      dynamicFormData.length > 0 &&
      dynamicFormData[0]?.elements
    ) {
      formInfo = dynamicFormData[0].elements.reduce((acc, item) => {
        if (
          item?.properties?.label &&
          item?.properties?.fieldname &&
          formDetails.hasOwnProperty(item.properties.fieldname)
        ) {
          // acc[item.properties.label] = formDetails[item.properties.fieldname];
          acc[item.properties.fieldname] =
            formDetails[item.properties.fieldname];
        }
        return acc;
      }, {});
      if (!isFormHidden && !isExplicitlyAnonymous) {
        firstName = formInfo["First Name"] ?? "";
        lastName = formInfo["Last Name"] ?? "";
        email = formInfo[this.findEmailKey(formInfo) || "Email"] ?? "";
        phone = formInfo["Phone Number"] ?? "";
      }
    }

    // Construct consumerData
    const baseConsumerData = {
      isAnonymous,
      ...formInfo, // Spread validated formInfo
    };
    let consumerData = userConfig
      ? {
          ...baseConsumerData,
          additionalInfo: {
            ...(JSON.parse(sessionStorage.getItem("userContext"))?.consumerData
              ?.additionalInfo || {}),
            ...userConfig,
          },
        }
      : baseConsumerData;
    let user = s360ConfigService.config.user;

    // if user is a string, parse once
    if (typeof user === "string") {
      try {
        user = JSON.parse(user);
      } catch (e) {
        console.error("Invalid user JSON:", e);
        user = {};
      }
    }

    let userContext = sessionStorage.getItem("userContext");

    if (userContext) {
      try {
        userContext = JSON.parse(userContext);
      } catch (e) {
        console.error("Invalid userContext JSON:", e);
        userContext = {};
      }
    } else {
      userContext = baseConsumerData;
    }

    const userInfoFromParams =
      s360ConfigService.config.userInfoFromParams || {};

    // Strip any accidentally nested consumerData/additionalInfo from previous sessions
    const {
      consumerData: _nested1,
      additionalInfo: _nested2,
      ...cleanSessionAdditionalInfo
    } = userContext?.consumerData?.additionalInfo || {};
    const {
      consumerData: _nested3,
      additionalInfo: _nested4,
      ...cleanCurrentAdditionalInfo
    } = consumerData.additionalInfo || {};

    consumerData.additionalInfo = {
      ...cleanSessionAdditionalInfo,
      ...user,
      ...cleanCurrentAdditionalInfo,
      ...userInfoFromParams,
      ...formInfo,
      isAnonymous,
      Timezone: s360ConfigService.config.userInfo.timeZone,
      OS: s360ConfigService.config.userInfo.os,
      sourceHost: s360ConfigService.config?.sourceHost || window.location.host,
      isMobile: false,
      sourceDomain: window.location.hostname,
    };

    consumerData.isMobile = false;
    consumerData.sourceDomain = window.location.hostname;

    const requestFromValue = this.getFromUuidValue();

    try {
      // Make the API call
      const context = {
        isAnonymous: true,
        isMobile: false,
        sourceDomain: window.location.hostname,
        consumerData,
      };
      context.consumerData.isAnonymous = true;
      if (isOffline) {
        context["isOffline"] = true;
      }

      // Merge sessionStorage context with new context, prioritizing new form data
      let sessionContext = null;
      try {
        const sessionContextStr = sessionStorage.getItem("userContext");
        if (sessionContextStr) {
          sessionContext = JSON.parse(sessionContextStr);
        }
      } catch (e) {
        console.error("Failed to parse userContext from sessionStorage:", e);
      }

      let contextToSend;
      if (sessionContext) {
        // Destructure to prevent nested consumerData from leaking into additionalInfo
        const {
          additionalInfo: sessionAdditionalInfo,
          ...sessionConsumerRest
        } = sessionContext.consumerData || {};
        const {
          additionalInfo: currentAdditionalInfo,
          ...currentConsumerRest
        } = context.consumerData || {};

        // Merge contexts, with new context taking precedence
        contextToSend = {
          ...sessionContext,
          ...context,
          consumerData: {
            ...sessionConsumerRest,
            ...currentConsumerRest,
            additionalInfo: {
              ...sessionAdditionalInfo,
              ...currentAdditionalInfo,
              sourceHost:
                s360ConfigService.config?.sourceHost || window.location.host,
            },
          },
        };
      } else {
        const {
          additionalInfo: currentAdditionalInfo,
          ...currentConsumerRest
        } = context.consumerData || {};

        // No session context, use the new context
        contextToSend = {
          ...context,
          consumerData: {
            ...currentConsumerRest,
            additionalInfo: {
              ...currentAdditionalInfo,
              sourceHost:
                s360ConfigService.config?.sourceHost || window.location.host,
            },
          },
        };
      }

      const response = await axios.post(
        s360ConfigService.config.location + endpoints.POST_CHAT_INIT,
        {
          context: contextToSend,
          tenant,
          campaignId,
          // email: consumerData.email, // Use email from consumerData
          from: requestFromValue, // Use the prioritized 'from' value for the request
          // name: `${consumerData.firstName} ${consumerData.lastName}`.trim(), // Use names from consumerData
          address: s360ConfigService.config._id,
        },
        this.thrioAuthHeaders, // Use getter for headers
      );

      // --- New Code: Process the response ---
      // Check if the response was successful (status 2xx) and has data
      if (
        response &&
        response.data &&
        response.status >= 200 &&
        response.status < 300
      ) {
        if (!s360ConfigService?.config?.connectToNextiva) {
          const context = response.data.data.context;

          statsigLogEvent(LIVECHAT_LOG_EVENTS.START_CHAT, {
            [LIVECHAT_LOG_KEYS.action]: LIVECHAT_LOG_ACTIONS.START_CHAT,
            [LIVECHAT_LOG_KEYS.widgetConfigId]:
              s360ConfigService?.config?.widgetconfigId,
            [LIVECHAT_LOG_KEYS.campaignId]:
              s360ConfigService?.config?.campaignId,
            [LIVECHAT_LOG_KEYS.timeStamp]: Date.now(),
            [LIVECHAT_LOG_KEYS.isAnonymous]: context.isAnonymous,
            [LIVECHAT_LOG_KEYS.fromRequestValue]: requestFromValue,
          });
        }
        // Sanitize context before saving to prevent recursive nesting
        const responseContext = response.data.data.context || {};
        if (responseContext.consumerData?.additionalInfo) {
          const {
            consumerData: _nestedCD,
            additionalInfo: _nestedAI,
            ...cleanAdditionalInfo
          } = responseContext.consumerData.additionalInfo;
          responseContext.consumerData.additionalInfo = cleanAdditionalInfo;
        }
        sessionStorage.setItem("userContext", JSON.stringify(responseContext));
        // const responseFromValue = response.data.from;
        // const widgetConfigId = s360ConfigService.config.widgetconfigId;

        // if (responseFromValue && widgetConfigId) {
        //   // Set the cookie using the 'from' value FROM THE API RESPONSE
        //   setCookieForPrimaryIdentification(responseFromValue, widgetConfigId);
        // } else {
        //   console.warn(
        //     "Initiated chat, but could not set primary identification cookie: 'from' value missing in API response or widgetConfigId missing.",
        //     response.data
        //   );
        //   // Optional Fallback: If the response 'from' is missing, maybe set the calculated one?
        //   if (
        //     this.getCalculatedFromValue(
        //       primaryIdentificationValueFromFormConfig
        //     ) &&
        //     widgetConfigId
        //   ) {
        //     setCookieForPrimaryIdentification(
        //       this.getCalculatedFromValue(
        //         primaryIdentificationValueFromFormConfig
        //       ),
        //       widgetConfigId
        //     );
        //   }
        // }
      } else {
        // Handle non-successful responses if needed (axios might throw for non-2xx, depends on config)
        console.warn(
          "Chat initiation request completed but response indicates potential issue or missing data.",
          response,
        );
        // Fallback: Set the cookie with the calculated value even if response is suboptimal?
        // const widgetConfigId = s360ConfigService.config.widgetconfigId;
        // if (
        //   this.getCalculatedFromValue(
        //     primaryIdentificationValueFromFormConfig
        //   ) &&
        //   widgetConfigId
        // ) {
        //   setCookieForPrimaryIdentification(
        //     this.getCalculatedFromValue(
        //       primaryIdentificationValueFromFormConfig
        //     ),
        //     widgetConfigId
        //   );
        // }
      }
      // --- End New Code ---

      // Return the original response object so the caller can use it
      return response;
    } catch (error) {
      console.error("Error during initiateChat API call:", error);
      // Decide if we should still try to set a cookie based on calculated value on error
      const widgetConfigId = s360ConfigService.config.widgetconfigId;
      // if (
      //   this.getCalculatedFromValue(primaryIdentificationValueFromFormConfig) &&
      //   widgetConfigId
      // ) {
      //   setCookieForPrimaryIdentification(
      //     this.getCalculatedFromValue(primaryIdentificationValueFromFormConfig),
      //     widgetConfigId
      //   );
      // }
      // Re-throw the error so the caller knows the operation failed
      throw error;
    }
  }

  triggerChatRequest(chatWorkItemId) {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !chatWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or parameters for triggerChatRequest.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for triggerChatRequest"),
      );
    }
    const effectiveWorkItemId =
      chatWorkItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    const url =
      s360ConfigService.config.location +
      endpoints.TRIGGER_CHAT_ACTION.replace(
        ":chatWorkItemId",
        effectiveWorkItemId,
      );

    return axios.post(url, {}, this.thrioAuthHeaders).then((res) => {
      // Check specifically for 204 No Content status
      if (res.status === 204) {
        s360ConfigService.config.anonymousClosed = false; // Assuming this config property exists and is used
        try {
          sessionStorage.setItem("subscribed", "true"); // Store as string
        } catch (e) {
          console.error("Failed to set sessionStorage item 'subscribed':", e);
        }
      }
      // Return the original response for potential further chaining/handling
      return res;
    });
    // Add a catch block for network/API errors
    // .catch(error => {
    //     console.error("Error triggering chat request:", error);
    //     throw error; // Re-throw to allow caller to handle
    // });
  }

  keepAlive(chatWorkItemId) {
    const effectiveWorkItemId =
      chatWorkItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    // Basic checks before attempting API call
    if (!navigator.onLine) {
      console.warn("KeepAlive skipped: Navigator is offline.");
      return Promise.resolve(); // Resolve promise immediately without error
    }
    // Ensure config and chatWorkItemId are present
    if (!effectiveWorkItemId) {
      console.warn(
        "KeepAlive skipped: Missing configuration, chatWorkItemId, or thrioConfig is empty.",
      );
      return Promise.resolve(); // Resolve promise immediately without error
    }

    const url =
      s360ConfigService.config.location +
      endpoints.KEEP_ALIVE.replace(":chatWorkItemId", effectiveWorkItemId);

    return axios.post(url, {}, this.thrioAuthHeaders);
    // Add a catch block for network/API errors if needed
    // .catch(error => {
    //     // Decide how to handle keep-alive errors (e.g., log silently)
    //     console.warn("KeepAlive request failed:", error);
    //     // Don't necessarily reject/throw unless critical
    // });
  }

  terminateChat(chatWorkItemId) {
    const effectiveWorkItemId =
      chatWorkItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !effectiveWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or parameters for terminateChat.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for terminateChat"),
      );
    }
    const url =
      s360ConfigService.config.location +
      endpoints.TRIGGER_CHAT_ACTION.replace(
        ":chatWorkItemId",
        effectiveWorkItemId,
      );

    return axios.delete(url, this.thrioAuthHeaders);
    // Add a catch block for network/API errors
    // .catch(error => {
    //     console.error("Error terminating chat:", error);
    //     throw error; // Re-throw to allow caller to handle
    // });
  }

  getCurrentTicketId(currentWorkitemId) {
    const effectiveWorkItemId =
      currentWorkitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !effectiveWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or parameters for getCurrentTicketId.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for getCurrentTicketId"),
      );
    }
    const url =
      s360ConfigService.config.location +
      endpoints.GET_CHAT_HISTORY.replace(":workitemId", effectiveWorkItemId);

    return axios.get(url, this.thrioAuthHeaders);
  }

  getFile(workitemId, filename) {
    const effectiveWorkItemId =
      workitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token ||
      !effectiveWorkItemId ||
      !filename
    ) {
      console.error(
        "APIService Error: Missing configuration or parameters for getFile.",
      );
      // Return a placeholder or throw an error based on how this URL is used
      return "#error-missing-config";
    }
    // Construct the URL directly for use in image src or link href
    return (
      s360ConfigService.config.location +
      endpoints.GET_FILE.replace(":workitemId", effectiveWorkItemId) +
      // Use URLSearchParams for robust query string construction
      `?${new URLSearchParams({
        filename: filename,
        token: s360ConfigService.config.token,
      }).toString()}`
    );

    // Original commented-out axios call - useful if file content needs processing in JS
    // return axios.get(
    //   s360ConfigService.config.location + endpoints.GET_FILE.replace(':messageId', chatWorkItemId), // Assuming messageId was placeholder for workitemId
    //   {
    //     params: {
    //       filename,
    //       token: s360ConfigService.config.token,
    //       // 'content-type': 'image/jpg' // Content-type is usually response header, not request param
    //     },
    //     // Might need responseType: 'blob' or 'arraybuffer' depending on usage
    //     ...this.thrioAuthHeaders // Pass auth headers if required by the endpoint
    //   }
    // );
  }

  setTyping(chatWorkItemId) {
    const effectiveWorkItemId =
      chatWorkItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !effectiveWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or parameters for setTyping.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for setTyping"),
      );
    }
    const url =
      s360ConfigService.config.location +
      endpoints.SET_TYPING.replace(":chatWorkItemId", effectiveWorkItemId);
    return axios.post(
      url,
      {
        fromId: null, // Assuming null is correct, or needs a value?
        toId: effectiveWorkItemId,
        type: "CLIENT", // Hardcoded type
      },
      this.thrioAuthHeaders,
    );
  }

  sendMessage(formData, wrkItemId) {
    const effectiveWorkItemId =
      wrkItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?._id
    ) {
      console.error("APIService Error: Missing configuration for sendMessage.");
      return Promise.reject(new Error("Missing configuration for sendMessage"));
    }
    // Determine workitemId, prioritize passed param, then resumed, then config fallback
    const workItemId =
      wrkItemId ||
      effectiveWorkItemId ||
      s360ConfigService.resumedWorkitemId ||
      s360ConfigService?.config?.workitemId;
    if (!workItemId) {
      console.error("APIService Error: Missing workitemId for sendMessage.");
      return Promise.reject(new Error("Missing workitemId for sendMessage"));
    }

    // Ensure formData is an object (if expecting key-value pairs) or FormData instance
    if (!(formData instanceof FormData) && typeof formData !== "object") {
      console.error("APIService Error: Invalid formData type for sendMessage.");
      return Promise.reject(new Error("Invalid formData type for sendMessage"));
    }

    // Add configId to payload if it's a plain object
    // If formData is a FormData instance, appending might need care
    if (!(formData instanceof FormData)) {
      formData.configId = s360ConfigService.config._id;
    } else {
      // If it's FormData, check if configId should be appended
      // formData.append('configId', s360ConfigService.config._id); // Example if needed
    }

    const url =
      s360ConfigService.config.location +
      endpoints.SEND_MESSAGE.replace(":workItemId", workItemId);

    return axios.post(url, formData, this.thrioAuthHeaders);
  }

  updateMessageStatus(workItemId, messageId, statuses) {
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token
    ) {
      return Promise.reject("Missing configuration for updateMessageStatus");
    }
    // Determine workitemId, prioritize passed param, then config fallback
    const effectiveWorkItemId =
      workItemId || s360ConfigService?.config?.workitemId;
    if (
      !effectiveWorkItemId ||
      !messageId ||
      !statuses ||
      !Array.isArray(statuses)
    ) {
      return Promise.reject("Missing parameters for updateMessageStatus");
    }

    const url =
      s360ConfigService.config.location +
      endpoints.UPDATE_MESSAGE_STATUS.replace(
        ":workItemId",
        effectiveWorkItemId,
      ).replace(":messageId", messageId);
    const payload = { statuses }; // Ensure statuses is an array

    // Use the getter for standard auth headers
    return axios.post(url, payload, this.thrioAuthHeaders);
  }

  getContact(name) {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !name) {
      console.error(
        "APIService Error: Missing configuration or name parameter for getContact.",
      );
      return Promise.reject(
        new Error("Missing configuration or name parameter for getContact"),
      );
    }
    const encodedName = encodeURIComponent(name); // Encode the name parameter
    const url =
      s360ConfigService.config.location +
      endpoints.GET_CONTACT +
      `?q=${encodedName}`;
    return axios.get(url, this.thrioAuthHeaders);
  }

  getChatStaleStatus(workitemId) {
    const effectiveWorkItemId =
      workitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );

    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !effectiveWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or workitemId for getChatStaleStatus.",
      );
      return Promise.reject(
        new Error("Missing configuration or workitemId for getChatStaleStatus"),
      );
    }
    const url =
      s360ConfigService.config.location +
      endpoints.CHAT_STATUS.replace(":chatWorkItemId", effectiveWorkItemId);

    // Try to fetch with current token; on 403, refresh token, show close screen, and retry once
    return axios.get(url, this.thrioAuthHeaders).catch(async (err) => {
      const status = err?.response?.status;
      if (status === 403) {
        try {
          await this.refreshAuthToken();
        } catch (refreshErr) {
          // Even if refresh fails, fall through to dispatch event and rethrow original error
        }
        try {
          // Trigger existing close screen flow via the global event used by WebSocket path
          if (typeof window !== "undefined" && window?.dispatchEvent) {
            const evt = new CustomEvent("WorkitemTerminatedNotification", {
              detail: {
                name: "WorkitemTerminatedNotification",
                reason: "AUTH_FORBIDDEN",
                workitemId: effectiveWorkItemId,
              },
            });
            window.dispatchEvent(evt);
          }
        } catch (_) {}

        // Retry the call once with the refreshed token
        return axios.get(url, this.thrioAuthHeaders);
      }
      throw err;
    });
  }

  // Refresh Thrio auth token and store it in config
  async refreshAuthToken() {
    const base = s360ConfigService?.config?.location;
    const key = s360ConfigService?.config?.encodedIdAndTenant;
    if (!base || !key) {
      console.error(
        "APIService Error: Missing location or encodedIdAndTenant for refreshAuthToken.",
      );
      throw new Error("Missing configuration for refreshAuthToken");
    }
    const url =
      base +
      endpoints.GET_AUTH_TOKEN.replace(":configKey", encodeURIComponent(key));

    try {
      const res = await axios.get(url);
      const data = res?.data || {};
      if (data?.token) {
        s360ConfigService.config.token = data.token;
      }
      if (data?.location) {
        s360ConfigService.config.location = data.location;
      }
      return data?.token || null;
    } catch (e) {
      console.error("Token refresh failed:", e?.response || e);
      throw e;
    }
  }

  getContactHistory(contactId, fromDate = null, limit = 100) {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !contactId) {
      console.error(
        "APIService Error: Missing configuration or contactId for getContactHistory.",
      );
      return Promise.reject(
        new Error("Missing configuration or contactId for getContactHistory"),
      );
    }
    const params = new URLSearchParams({ limit: limit.toString() });
    if (fromDate) {
      params.append("fromDate", fromDate); // Add fromDate if provided
    }
    const url = `${
      s360ConfigService.config.location
    }${endpoints.GET_CONTACT_HISTORY.replace(
      ":contactId",
      contactId,
    )}?${params.toString()}`;
    return axios.get(url, this.thrioAuthHeaders);
  }

  getPrimaryIdentifierHistory(
    primaryIdentificationValue,
    fromDate = null,
    limit = 100,
  ) {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !primaryIdentificationValue) {
      console.error(
        "APIService Error: Missing configuration or primaryIdentificationValue for getPrimaryIdentifierHistory.",
      );
      return Promise.reject(
        new Error(
          "Missing configuration or primaryIdentificationValue for getPrimaryIdentifierHistory",
        ),
      );
    }
    const params = new URLSearchParams({
      usingPrimaryIdentifier: "true",
      limit: limit.toString(),
    });
    if (fromDate) {
      params.append("fromDate", fromDate);
    }
    // Encode the identifier value in case it contains special characters
    const encodedIdentifier = encodeURIComponent(primaryIdentificationValue);
    const url = `${
      s360ConfigService.config.location
    }${endpoints.GET_PRIMARYIDENTIFICATION_HISTORY.replace(
      ":primaryIdentificationValue",
      encodedIdentifier,
    )}?${params.toString()}`;
    return axios.get(url, this.thrioAuthHeaders);
  }

  getTicketsListHistory(primaryIdentificationValue, limit = 10) {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !primaryIdentificationValue) {
      console.error(
        "APIService Error: Missing configuration or primaryIdentificationValue for getPrimaryIdentifierHistory.",
      );
      return Promise.reject(
        new Error(
          "Missing configuration or primaryIdentificationValue for getPrimaryIdentifierHistory",
        ),
      );
    }

    const params = new URLSearchParams({
      usingPrimaryIdentifier: "true",
      limit: limit.toString(),
    });

    // Encode the identifier value in case it contains special characters
    const url = `${
      s360ConfigService.config.location
    }${endpoints.GET_TICKET_LIST_HISTORY.replace(
      ":userPrimaryId",
      primaryIdentificationValue,
    )}?${params.toString()}`;
    return axios.get(url, this.thrioAuthHeaders);
  }

  getChatHistory(workitemId, fromDate = null, limit = 100) {
    const effectiveWorkItemId =
      workitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );

    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !effectiveWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or workitemId for getChatHistory.",
      );
      return Promise.reject(
        new Error("Missing configuration or workitemId for getChatHistory"),
      );
    }
    const params = new URLSearchParams({ limit: limit.toString() });
    if (fromDate) {
      params.append("fromDate", fromDate);
    }
    const url = `${
      s360ConfigService.config.location
    }${endpoints.GET_CHAT_HISTORY.replace(
      ":workitemId",
      effectiveWorkItemId,
    )}?${params.toString()}`;
    return axios.get(url, this.thrioAuthHeaders);
  }

  getTicketHistory(ticketId,options = {}) {
    const {
      limit = 100,
      fromDate = null,
      autoRefresh = false,
      workitemId = null,
    } = options || {};

    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !ticketId) {
      console.error(
        "APIService Error: Missing configuration or ticketid for getChatHistory.",
      );
      return Promise.reject(
        new Error("Missing configuration or workitemId for getChatHistory"),
      );
    }

    const params = new URLSearchParams();
    if (limit !== undefined && limit !== null) {
      params.set("limit", String(limit));
    }
    if (fromDate) {
      params.append("fromDate", String(fromDate));
    }
    if (autoRefresh) {
      params.append("autoRefresh", "true");
    }
    if (workitemId) {
      params.append("workitemId", String(workitemId));
    }
    const primaryIdentification =
      getCookieForContactDetails(s360ConfigService?.config?.widgetconfigId) ||
      this.getUUidIfNoPrimaryIdentification();
    params.append("uuid", primaryIdentification);
  

    const query = params.toString();
    const url = `${
      s360ConfigService.config.location
    }${endpoints.GET_TICKET_HISTORY.replace(":ticketId", ticketId)}${
      query ? `?${query}` : ""
    }`;
    return axios.get(url, this.thrioAuthHeaders);
  }

  getSurveyData = (surveyId) => {
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token ||
      !surveyId
    ) {
      console.error(
        "APIService Error: Missing configuration or surveyId for getSurveyData.",
      );
      return Promise.reject(
        new Error("Missing configuration or surveyId for getSurveyData"),
      );
    }
    const domain = s360ConfigService.config.location;
    const url = `${domain}/data/api/types/survey/${surveyId}`;
    // Use the getter for standard auth headers + content type
    const config = {
      headers: {
        ...this.thrioAuthHeaders.headers, // Spread auth header
        "content-type": "application/json",
      },
    };
    return axios.get(url, config);
  };

  // Private Method - Should ideally not be directly called from outside the class
  logIn = (data) => {
    // Use config for domain, fallback if needed, ensure required props exist
    const logHost = s360ConfigService?.config?.loghost;
    const configId = s360ConfigService?.config?.configId;
    const host = s360ConfigService?.config?.host;
    const token = s360ConfigService?.config?.token; // Needed for bearer token generation

    if (!logHost || !configId || !host || !token) {
      console.error(
        "APIService Error: Missing configuration for logIn (loghost, configId, host, or token).",
      );
      return Promise.reject(new Error("Missing configuration for logIn"));
    }

    const domain = logHost; // Assuming loghost is the correct domain
    const isPreview = s360ConfigService?.config?.preview;
    const baseEndpoint = isPreview
      ? "/bmapi/webchatv2/preview/login"
      : "/bmapi/webchatv2/login";
    const url = `https://${domain}${baseEndpoint}`;

    const nonce = uuid();
    const timestamp = new Date().getTime();
    const bearerToken = this.generateBearerToken(url, nonce, timestamp); // Pass correct URL

    if (!bearerToken) {
      // Check if token generation failed
      console.error(
        "APIService Error: Failed to generate bearer token for logIn.",
      );
      return Promise.reject(
        new Error("Failed to generate bearer token for logIn"),
      );
    }

    const config = {
      headers: {
        Authorization: bearerToken,
        "content-type": "application/json",
      },
      params: { nonce, timestamp }, // Use shorthand
    };

    // Prepare payload
    const payload = { ...data }; // Clone input data
    payload.configId = configId;
    payload.host = host;

    // Get user config safely, checking session storage as fallback
    let userConfig = s360ConfigService.getConfig()?.s360UserConfig;
    if (!userConfig || Object.keys(userConfig).length === 0) {
      try {
        const storedConfig = sessionStorage.getItem(
          `s360-user-config-${configId}`,
        );
        if (storedConfig) {
          userConfig = JSON.parse(storedConfig);
        }
      } catch (e) {
        console.error("Error parsing user config from sessionStorage:", e);
      }
    }
    payload.user = userConfig || {}; // Ensure user is at least an empty object

    // Handle anonymous login
    if (s360ConfigService.config.anonymous) {
      // Generate a consistent anonymous ID
      const clientHostId = s360ConfigService.clientHostId; // Assuming this exists
      const anonymousBrowserId = `${configId}-${clientHostId || nonce}`; // Use nonce as fallback

      payload.form = payload.form || {}; // Ensure form object exists
      payload.form.name = "anonymous"; // Indicate anonymous type
      payload.form.unId = isPreview
        ? s360ConfigService?.config?.userId
        : anonymousBrowserId; // Unique ID for anonymous

      // The commented-out logic for setting form fields based on primaryField seems complex
      // and might be handled differently now. If needed, it should be revisited.
      // For now, just sending name and unId for anonymous.
    }

    return axios.post(url, payload, config);
  };

  uploadMedia = (fileToUpload, mediaType, sessionId) => {
    // Ensure necessary config values are present
    const logHost = s360ConfigService?.config?.loghost;
    const configId = s360ConfigService?.config?.configId;
    const token = s360ConfigService?.config?.token;

    if (!logHost || !configId || !token || !fileToUpload || !sessionId) {
      console.error(
        "APIService Error: Missing configuration or parameters for uploadMedia.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for uploadMedia"),
      );
    }

    const url = `https://${logHost}/bmapi/jabber/upload`;
    const nonce = uuid();
    const timestamp = new Date().getTime();
    const bearerToken = this.generateBearerToken(
      url,
      nonce,
      timestamp,
      mediaType,
    ); // Pass mediaType here

    if (!bearerToken) {
      // Check if token generation failed
      console.error(
        "APIService Error: Failed to generate bearer token for uploadMedia.",
      );
      return Promise.reject(
        new Error("Failed to generate bearer token for uploadMedia"),
      );
    }

    const config = {
      headers: {
        Authorization: bearerToken,
        // Axios sets multipart/form-data boundary automatically when passed a FormData object
        // 'content-type': 'multipart/form-data', // Usually not needed here
      },
      params: { nonce, timestamp, configId, sessionId }, // Add sessionId to params
    };
    if (mediaType) {
      config.params.type = mediaType;
    }

    const formData = new FormData();
    formData.append("file", fileToUpload); // 'file' is the expected field name

    return axios.post(url, formData, config);
  };

  // Private Method - Should ideally not be directly called from outside the class
  generateBearerToken = (serviceUrl, nonce, timestamp, mediaType = null) => {
    // Ensure token is available
    const secretToken = s360ConfigService?.config?.token;
    if (!secretToken) {
      console.error(
        "APIService Error: Cannot generate bearer token, Thrio token is missing in config.",
      );
      return null; // Return null or throw error
    }

    try {
      // Construct the base string according to OAuth 1.0a-like signature generation (common pattern)
      // Ensure parameters are sorted alphabetically? (If required by backend, original code doesn't show sorting)
      // Example assumes specific parameter order: nonce, timestamp, then type if present
      let paramString = `nonce=${nonce}×tamp=${timestamp}`; // Changed 'xtamp' to 'timestamp' assuming typo
      if (mediaType) {
        paramString += `&type=${mediaType}`;
      }

      // Base String Format: HTTP_METHOD & URL_ENCODED_SERVICE_URL & URL_ENCODED_PARAM_STRING
      const baseString = `POST&${encodeURIComponent(
        serviceUrl,
      )}&${encodeURIComponent(paramString)}`;

      const sha1Signature = CryptoJS.HmacSHA1(baseString, secretToken);
      const encodedSignature = Base64.stringify(sha1Signature);

      return `Bearer ${encodedSignature}`;
    } catch (error) {
      console.error("Error generating bearer token:", error);
      return null;
    }
  };

  createTicketWithFormData = (data) => {
    // Ensure necessary config values are present
    const configId = s360ConfigService?.config?.configId;
    if (!configId || !data) {
      console.error(
        "APIService Error: Missing configId or data for createTicketWithFormData.",
      );
      return Promise.reject(
        new Error("Missing configId or data for createTicketWithFormData"),
      );
    }

    // Use environment variable or fallback for URL, ensure HTTPS
    const baseUrl =
      process.env.REACT_APP_TICKET_URL || "https://suitex.simplify360.com"; // Example fallback
    const url = `${baseUrl}/spring/jabber/email/notification`;

    const config = {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        // "Access-Control-Allow-Origin": "*", // This is a RESPONSE header, browser client cannot set it
      },
    };

    const payload = {
      data: data, // The form data itself
      configId: configId,
      // host: s360ConfigService.config.host, // Original code included host, check if needed
    };

    return axios.post(url, payload, config);
  };

  sendPreviewData = (text, componentId, previewSessionId) => {
    // Ensure necessary config values are present
    if (
      !s360ConfigService?.config?.location ||
      !previewSessionId ||
      typeof text === "undefined"
    ) {
      console.error(
        "APIService Error: Missing configuration or parameters for sendPreviewData.",
      );
      return Promise.reject(
        new Error("Missing configuration or parameters for sendPreviewData"),
      );
    }
    const decodedSessionId = decodeURIComponent(previewSessionId);

    // Construct payload based on whether componentId is present
    const messagePayload = componentId
      ? { textMsg: text, componentId: componentId }
      : { textMsg: text };

    const payload = {
      previewSessionId: decodedSessionId,
      message: messagePayload,
    };

    const url = `${s360ConfigService.config.location}/botengine/api/preview/chats/messages`;
    return axios.post(url, payload, this.thrioAuthHeaders);
  };

  sendPreviewDataFiles = (formData, previewSessionId) => {
    if (!s360ConfigService?.config?.location) {
      console.error(
        "APIService Error: Missing location configuration for sendPreviewDataFiles.",
      );
      return Promise.reject(
        new Error("Missing location configuration for sendPreviewDataFiles"),
      );
    }
    if (!(formData instanceof FormData)) {
      console.error(
        "APIService Error: Invalid formData type for sendPreviewDataFiles, expected FormData.",
      );
      return Promise.reject(
        new Error("Invalid formData type for sendPreviewDataFiles"),
      );
    }

    if (
      previewSessionId === undefined ||
      previewSessionId === null ||
      previewSessionId === ""
    ) {
      console.error(
        "APIService Error: previewSessionId argument missing for sendPreviewDataFiles.",
      );
      return Promise.reject(
        new Error("previewSessionId argument missing for sendPreviewDataFiles"),
      );
    }

    let tempPreviewSessionID = decodeURIComponent(previewSessionId);

    formData.append(
      "object",
      JSON.stringify({
        previewSessionId: tempPreviewSessionID, // Use the decoded session ID
        message: {
          textMsg: "", // Include the empty message structure as in your snippet
        },
      }),
    );

    // REMOVED the old check: if (!formData.has("previewSessionId"))
    // because previewSessionId is now part of the 'object' JSON field

    // --- END OF MODIFIED LOGIC ---

    const url = `${s360ConfigService.config.location}/botengine/api/preview/chats/messages`;
    // When sending FormData, let axios set the Content-Type header automatically
    const headers = { ...this.thrioAuthHeaders.headers };
    // delete headers['content-type']; // Remove if it was set in thrioAuthHeaders
    // axios handles FormData correctly, no change needed here

    return axios.post(url, formData, { headers });
  };

  sendOfflineForm = (fields) => {
    // Ensure necessary config values are present
    const configId = s360ConfigService?.config?.configId;
    if (
      !configId ||
      !fields ||
      !fields["email"]?.value ||
      !fields["message"]?.value
    ) {
      console.error(
        "APIService Error: Missing configId or required fields for sendOfflineForm.",
      );
      return Promise.reject(
        new Error("Missing configId or required fields for sendOfflineForm"),
      );
    }

    // Use environment variable for base URL if available, ensure it's defined
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      console.error(
        "APIService Error: REACT_APP_API_BASE_URL environment variable not set.",
      );
      return Promise.reject(new Error("API base URL configuration is missing"));
    }
    const url = `${apiBaseUrl}/apps/api/v2/livechat/offlineMailSender?configId=${configId}`;

    const config = {
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
      },
    };

    // Extract values safely
    const payload = {
      email: fields["email"].value,
      message: fields["message"].value,
    };

    return axios.post(url, payload, config);
  };

  sendHomeMenu = (workitemId) => {
    const effectiveWorkItemId =
      workitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );

    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !effectiveWorkItemId) {
      console.error(
        "APIService Error: Missing configuration or workitemId for sendHomeMenu.",
      );
      return Promise.reject(
        new Error("Missing configuration or workitemId for sendHomeMenu"),
      );
    }
    const action = "start-state"; // Hardcoded action
    const url =
      s360ConfigService.config.location +
      endpoints.GO_HOME.replace(":chatWorkItemId", effectiveWorkItemId).replace(
        ":action",
        action,
      );
    return axios.post(url, {}, this.thrioAuthHeaders);
  };

  sendHomeMenuPreview = (previewSessionId) => {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !previewSessionId) {
      console.error(
        "APIService Error: Missing configuration or previewSessionId for sendHomeMenuPreview.",
      );
      return Promise.reject(
        new Error(
          "Missing configuration or previewSessionId for sendHomeMenuPreview",
        ),
      );
    }
    const action = "start-state"; // Hardcoded action
    const url =
      s360ConfigService.config.location +
      endpoints.GO_HOME_PREVIEW.replace(":action", action);
    const payload = {
      previewSessionId: decodeURIComponent(previewSessionId),
    };
    return axios.post(url, payload, this.thrioAuthHeaders);
  };

  sendOfflineEmail(formDetails, captchaToken, dynamicFormData) {
    const payload = {
      configId: s360ConfigService?.config?.widgetconfigId,
      from: this.getFromUuidValue(),
      userFormData: formDetails,
      captchaToken: captchaToken,
    };
    const url = s360ConfigService.config.location + endpoints.OFFLINE_EMAIL;
    return axios.post(url, payload, this.thrioAuthHeaders);
  }

  getRegexExpressionFromId(validationId) {
    // Ensure necessary config values are present
    if (!s360ConfigService?.config?.location || !validationId) {
      console.error(
        "APIService Error: Missing configuration or validationId for getRegexExpressionFromId.",
      );
      return Promise.reject(
        new Error(
          "Missing configuration or validationId for getRegexExpressionFromId",
        ),
      );
    }

    const url =
      s360ConfigService.config.location +
      endpoints.GET_REGEX_EXPRESSION.replace(":validationId", validationId);
    return axios.get(url, this.thrioAuthHeaders);
  }

  chatReadyOfflineForm(workitemId) {
    const effectiveWorkItemId =
      workitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );

    const url =
      s360ConfigService.config.location +
      endpoints.TRIGGER_CHAT_ACTION.replace(
        ":chatWorkItemId",
        effectiveWorkItemId,
      );
    return axios.post(url, {}, this.thrioAuthHeaders);
  }

  editMessage(workItemId, engagementDataId, message) {
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token
    ) {
      return Promise.reject("Missing configuration for updateMessageStatus");
    }
    const effectiveWorkItemId =
      workItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    if (!effectiveWorkItemId || !engagementDataId || !message) {
      console.error("APIService Error: Missing parameters for editMessage.");
      return Promise.reject(
        new Error("Missing parameters for updateMessageStatus"),
      );
    }

    const url =
      s360ConfigService.config.location +
      endpoints.EDIT_MESSAGE.replace(
        ":workItemId",
        effectiveWorkItemId,
      ).replace(":engagementDataId", engagementDataId);
    const payload = { message };

    // Use the getter for standard auth headers
    return axios.patch(url, payload, this.thrioAuthHeaders);
  }

  deleteMessage(workItemId, engagementDataId) {
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.token
    ) {
      console.error(
        "APIService Error: Missing configuration for updateMessageStatus.",
      );
      return Promise.reject(
        new Error("Missing configuration for updateMessageStatus"),
      );
    }
    const effectiveWorkItemId =
      workItemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    if (!effectiveWorkItemId || !engagementDataId) {
      console.error("APIService Error: Missing parameters for deleteMessage.");
      return Promise.reject(
        new Error("Missing parameters for updateMessageStatus"),
      );
    }

    const url =
      s360ConfigService.config.location +
      endpoints.DELETE_MESSAGE.replace(
        ":workItemId",
        effectiveWorkItemId,
      ).replace(":engagementDataId", engagementDataId);

    // Use the getter for standard auth headers
    return axios.delete(url, this.thrioAuthHeaders);
  }

  sendFaqFeedback(
    reviewThumbType,
    reviewSelectedReason,
    engagementDataId,
    ticketId,
    comment = "",
  ) {
    if (engagementDataId && ticketId) {
      const url = s360ConfigService.config.location + endpoints.FAQ_FEEDBACK;
      axios.post(
        url,
        {
          feedbackAction: "LLM_RESPONSE", // This will be a hard coded value
          rateReply: reviewThumbType,
          reason: reviewSelectedReason,

          requestId: engagementDataId,
          ticketId: ticketId,
          widgetConfigId: s360ConfigService?.config?.widgetconfigId,
        },
        this.thrioAuthHeaders,
      );
    }
  }

  getAgentImage(userId) {
    if (userId) {
      const url =
        s360ConfigService.config.location +
        endpoints.GET_AGENT_IMAGE.replace(":userId", userId);
      return axios.get(url, this.thrioAuthHeaders);
    }
  }

  getAIEmployeeDetails(aiEmployeeId) {
    if (aiEmployeeId) {
      const url =
        s360ConfigService.config.location +
        endpoints.GET_AI_EMPLOYEE_DETAILS.replace(
          ":aiEmployeeId",
          aiEmployeeId,
        );
      return axios.get(url, this.thrioAuthHeaders);
    }
  }

  getMediaUrl(workitemId, fileName) {
    const effectiveWorkItemId =
      workitemId ||
      localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
    if (effectiveWorkItemId && fileName) {
      const url =
        s360ConfigService.config.location +
        endpoints.GET_MEDIA_URL.replace(
          ":workitemId",
          effectiveWorkItemId,
        ).replace(":fileName", encodeURIComponent(fileName)); // Add URL encoding for filename
      const config = {
        headers: {
          ...this.thrioAuthHeaders.headers,
          "content-type": "application/json",
        },
      };
      return axios.get(url, config).catch((error) => {
        console.error(`Failed to get media URL for file: ${fileName}`, error);
        throw new Error(`File not found: ${fileName}`);
      });
    }
    return Promise.reject(new Error("Missing workitemId or fileName"));
  }
  //getAudio
  //`${s360ConfigService.config?.location}/lioncc/${s360ConfigService.config?.notification?.audio}`;
  //api call to get audio file
  getAudio() {
    if (
      !s360ConfigService?.config?.location ||
      !s360ConfigService?.config?.notification?.audio
    ) {
      console.error(
        "APIService Error: Missing configuration for getAudio (location or audio).",
      );
      return Promise.reject(new Error("Missing configuration for getAudio"));
    }
    const url = `${s360ConfigService.config.location}/lioncc/${s360ConfigService.config.notification.audio}`;
    return axios.get(url, { responseType: "blob" });
  }
} // End of APIService class

// Create and export the singleton instance
const apiService = new APIService();
export default apiService;
